import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 18A — Zoom Meeting SDK query/mutation hooks. Mirrors
 * workDocumentQueries.js's `request()` convention exactly.
 *
 * Fix (post-testing-agent, HIGH): FastAPI's own 422 validation-error body
 * shape is `{"detail": [{type, loc, msg, ...}, ...]}` — an ARRAY of
 * objects, not a string. Passing that straight into toast.error()/JSX
 * throws "Objects are not valid as a React child" and takes down the
 * whole page. `detailMessage()` is the ONE place every caller in this
 * module normalizes any shape of `detail` into a plain string.
 */
export function detailMessage(body, fallback = "Something went wrong — please try again.") {
  const detail = body?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => (typeof d === "string" ? d : d.msg || JSON.stringify(d))).join("; ") || fallback;
  if (typeof detail === "object") return detail.message || detail.error || JSON.stringify(detail);
  return fallback;
}

async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(detailMessage(body, "request_failed"));
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export function useMeetingConfig(workspaceId) {
  return useQuery({
    queryKey: ["zoom-meeting-config", workspaceId],
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/meeting-config`),
    enabled: !!workspaceId,
  });
}

export function useSaveMeetingConfig(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(`/api/rehearsal-room/workspaces/${workspaceId}/meeting-config`, { method: "PUT", body: JSON.stringify(payload) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["zoom-meeting-config", workspaceId] }),
  });
}

/** Not a useQuery — join material is short-lived (30 min) and must be
 * fetched fresh every time the user actually clicks "Join", never cached
 * or reused across renders. */
export function useRequestJoinMaterial(workspaceId, sesId) {
  return useMutation({
    mutationFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/sessions/${sesId}/meeting/join-material`, { method: "POST" }),
  });
}
